if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.cadenceIndicator = this.querySelector('[data-cadence-gallery-cue]');
        this.cadenceSlider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');

        this.cadenceTouchStartX = 0;
        this.cadenceTouchStartY = 0;
        this.cadenceTouchLastX = 0;
        this.cadenceTouchStartTime = 0;
        this.cadenceTouchStartScrollLeft = 0;
        this.cadenceTouchActiveIndex = 0;
        this.cadenceTouchAxis = null;
        this.cadenceTouchTracking = false;
        this.cadenceAnimationFrame = null;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));

        if (this.cadenceSlider) {
          this.cadenceSlider.addEventListener(
            'scroll',
            debounce(this.updateCadenceIndicator.bind(this), 40),
            { passive: true }
          );

          /*
            Mobile gallery: allow normal vertical page scrolling, but handle
            horizontal dragging ourselves so one gesture can move only one slide
            and native momentum cannot continue after release.
          */
          this.cadenceSlider.style.touchAction = 'pan-y';
          this.cadenceSlider.style.webkitOverflowScrolling = 'auto';
          this.cadenceSlider.style.scrollBehavior = 'auto';

          this.cadenceSlider.addEventListener(
            'touchstart',
            this.onCadenceTouchStart.bind(this),
            { passive: true }
          );

          this.cadenceSlider.addEventListener(
            'touchmove',
            this.onCadenceTouchMove.bind(this),
            { passive: false }
          );

          this.cadenceSlider.addEventListener(
            'touchend',
            this.onCadenceTouchEnd.bind(this),
            { passive: false }
          );

          this.cadenceSlider.addEventListener(
            'touchcancel',
            this.onCadenceTouchCancel.bind(this),
            { passive: true }
          );
        }

        this.updateCadenceIndicator();

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getCadenceSlides() {
        if (!this.cadenceSlider) return [];

        return Array.from(
          this.cadenceSlider.querySelectorAll(':scope > [data-media-id]')
        );
      }

      getCadenceClosestSlideIndex() {
        const slides = this.getCadenceSlides();
        if (!slides.length || !this.cadenceSlider) return 0;

        const sliderCenter =
          this.cadenceSlider.scrollLeft + this.cadenceSlider.clientWidth / 2;

        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(slideCenter - sliderCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      scrollCadenceToSlide(index, smooth = true) {
        const slides = this.getCadenceSlides();
        if (!slides.length || !this.cadenceSlider) return;

        const boundedIndex = Math.max(0, Math.min(slides.length - 1, index));
        const targetSlide = slides[boundedIndex];

        const unclampedTargetLeft =
          targetSlide.offsetLeft -
          (this.cadenceSlider.clientWidth - targetSlide.offsetWidth) / 2;

        const maximumScrollLeft =
          this.cadenceSlider.scrollWidth - this.cadenceSlider.clientWidth;

        const targetLeft = Math.max(
          0,
          Math.min(maximumScrollLeft, unclampedTargetLeft)
        );

        if (this.cadenceAnimationFrame) {
          cancelAnimationFrame(this.cadenceAnimationFrame);
          this.cadenceAnimationFrame = null;
        }

        if (!smooth) {
          this.cadenceSlider.scrollLeft = targetLeft;
          this.cadenceSlider.style.scrollSnapType =
            this.mql.matches ? '' : 'none';
          return;
        }

        const startLeft = this.cadenceSlider.scrollLeft;
        const distance = targetLeft - startLeft;
        const duration = 340;
        const startTime = performance.now();

        /*
          Keep native snapping disabled until the controlled transition is fully
          complete. Restoring it earlier causes the destination slide to flash
          briefly before the animation begins.
        */
        this.cadenceSlider.style.scrollSnapType = 'none';

        const easeOutCubic = (progress) =>
          1 - Math.pow(1 - progress, 3);

        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(1, elapsed / duration);
          const easedProgress = easeOutCubic(progress);

          this.cadenceSlider.scrollLeft =
            startLeft + distance * easedProgress;

          if (progress < 1) {
            this.cadenceAnimationFrame = requestAnimationFrame(animate);
            return;
          }

          this.cadenceSlider.scrollLeft = targetLeft;
          this.cadenceSlider.style.scrollSnapType =
            this.mql.matches ? '' : 'none';
          this.cadenceAnimationFrame = null;
          this.updateCadenceIndicator(slides[boundedIndex]);
        };

        this.cadenceAnimationFrame = requestAnimationFrame(animate);
      }

      onCadenceTouchStart(event) {
        if (
          this.mql.matches ||
          !this.cadenceSlider ||
          event.touches.length !== 1
        ) {
          return;
        }

        const touch = event.touches[0];

        this.cadenceTouchStartX = touch.clientX;
        this.cadenceTouchStartY = touch.clientY;
        this.cadenceTouchLastX = touch.clientX;
        this.cadenceTouchStartTime = performance.now();
        this.cadenceTouchStartScrollLeft = this.cadenceSlider.scrollLeft;
        this.cadenceTouchActiveIndex = this.getCadenceClosestSlideIndex();
        this.cadenceTouchAxis = null;
        this.cadenceTouchTracking = true;

        this.cadenceSlider.style.scrollSnapType = 'none';
      }

      onCadenceTouchMove(event) {
        if (
          !this.cadenceTouchTracking ||
          this.mql.matches ||
          !this.cadenceSlider ||
          event.touches.length !== 1
        ) {
          return;
        }

        const touch = event.touches[0];
        const deltaX = touch.clientX - this.cadenceTouchStartX;
        const deltaY = touch.clientY - this.cadenceTouchStartY;

        if (!this.cadenceTouchAxis) {
          if (Math.abs(deltaX) < 7 && Math.abs(deltaY) < 7) return;

          this.cadenceTouchAxis =
            Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        }

        if (this.cadenceTouchAxis !== 'x') return;

        event.preventDefault();

        this.cadenceTouchLastX = touch.clientX;

        /*
          Move directly with the finger. Because native horizontal scrolling is
          disabled, there is no post-release momentum to fight or correct.
        */
        this.cadenceSlider.scrollLeft =
          this.cadenceTouchStartScrollLeft - deltaX;
      }

      onCadenceTouchEnd(event) {
        if (
          !this.cadenceTouchTracking ||
          this.mql.matches ||
          !this.cadenceSlider
        ) {
          return;
        }

        const wasHorizontal = this.cadenceTouchAxis === 'x';
        const touch = event.changedTouches?.[0];
        const endX = touch ? touch.clientX : this.cadenceTouchLastX;
        const deltaX = endX - this.cadenceTouchStartX;
        const elapsed = Math.max(1, performance.now() - this.cadenceTouchStartTime);
        const velocity = Math.abs(deltaX) / elapsed;

        this.cadenceTouchTracking = false;
        this.cadenceTouchAxis = null;

        if (!wasHorizontal) {
          this.cadenceSlider.style.scrollSnapType = '';
          return;
        }

        event.preventDefault();

        const distanceThreshold = Math.max(
          34,
          this.cadenceSlider.clientWidth * 0.1
        );
        const isIntentionalSwipe =
          Math.abs(deltaX) >= distanceThreshold ||
          (Math.abs(deltaX) >= 18 && velocity >= 0.35);

        let targetIndex = this.cadenceTouchActiveIndex;

        if (isIntentionalSwipe) {
          targetIndex += deltaX < 0 ? 1 : -1;
        }

        requestAnimationFrame(() => {
          this.scrollCadenceToSlide(targetIndex, true);
        });
      }

      onCadenceTouchCancel() {
        if (!this.cadenceTouchTracking || !this.cadenceSlider) return;

        const targetIndex = this.cadenceTouchActiveIndex;

        this.cadenceTouchTracking = false;
        this.cadenceTouchAxis = null;
        requestAnimationFrame(() => {
          this.scrollCadenceToSlide(targetIndex, true);
        });
      }

      onSlideChanged(event) {
        this.updateCadenceIndicator(event.detail.currentElement);

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      updateCadenceIndicator(currentElement = null) {
        if (!this.cadenceIndicator) return;

        const squares = Array.from(
          this.cadenceIndicator.querySelectorAll('.cadence-gallery-cue__square')
        );
        const slider =
          this.cadenceSlider ||
          this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        const slides = slider
          ? Array.from(slider.querySelectorAll(':scope > [data-media-id]'))
          : [];

        if (squares.length !== 3 || slides.length < 2 || !slider) return;

        let currentIndex = -1;

        if (currentElement) {
          currentIndex = slides.indexOf(currentElement);
        }

        if (currentIndex < 0) {
          const sliderRect = slider.getBoundingClientRect();
          const sliderCenter = sliderRect.left + sliderRect.width / 2;
          let closestDistance = Number.POSITIVE_INFINITY;

          slides.forEach((slide, index) => {
            const rect = slide.getBoundingClientRect();
            const slideCenter = rect.left + rect.width / 2;
            const distance = Math.abs(slideCenter - sliderCenter);

            if (distance < closestDistance) {
              closestDistance = distance;
              currentIndex = index;
            }
          });
        }

        currentIndex = Math.max(0, currentIndex);
        const lastIndex = slides.length - 1;

        let activeSquare = 1;

        if (slides.length === 2) {
          activeSquare = currentIndex === 0 ? 0 : 2;
        } else if (currentIndex === 0) {
          activeSquare = 0;
        } else if (currentIndex === lastIndex) {
          activeSquare = 2;
        }

        squares.forEach((square, index) => {
          square.classList.toggle('is-active', index === activeSquare);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
